import React, { useEffect, useRef, useState, useCallback } from 'react';
import useFocusOnInit from '../../../hooks/useFocusOnInit';
import PropTypes from 'prop-types';
import styles from './InputDateTime.css';

const RIGHT_KEY_CODE = 39;
const LEFT_KEY_CODE = 37;
const UP_KEY_CODE = 38;
const DOWN_KEY_CODE = 40;

export const formatToTwoDigits = (number) => {
  let numStr = number.toString();
  if (numStr.length < 2) {
    numStr = '0' + numStr;
  }
  if (numStr.length > 2 && numStr.charAt(0) === '0') {
    return numStr.substring(1);
  }
  return numStr;
};

export const formatToFourDigits = (number) => {
  let numStr = number.toString();
  numStr = numStr.replace(/^0+/, '');
  while (numStr.length < 4) {
    numStr = '0' + numStr;
  }
  return numStr;
};

export const dayTransform = ({ value }) => {
  try {
    const number = Number(value);
    if (number >= 1 && number <= 31) {
      return formatToTwoDigits(number);
    }
    if (number > 31) {
      return '31';
    }
    return value || '';
  } catch (e) {
    return '';
  }
};

export const monthTransform = ({ value }) => {
  try {
    const number = Number(value);
    if (number >= 1 && number <= 12) {
      return formatToTwoDigits(number);
    }
    if (number > 12) {
      return '12';
    }
    return value || '';
  } catch (e) {
    return '';
  }
};

export const yearTransform = ({ value }) => {
  return String(value ?? '').replace(/\D/g, '').slice(0, 4);
};

export const getDayMonthYear = (date) => {
  let day = date?.split('-')[2];
  let month = date?.split('-')[1];
  let year = date?.split('-')[0];

  if (!isNaN(parseInt(day))) {
    day = formatToTwoDigits(parseInt(day));
  } else day = '';

  if (!isNaN(parseInt(month))) {
    month = formatToTwoDigits(parseInt(month));
  } else month = '';

  if (!isNaN(parseInt(year))) {
    year = parseInt(year);
  } else year = '';

  return {
    day,
    month,
    year,
  };
};

const focusNextElement = (value) => {
  const regex = /^00\d{1}$/;
  return regex.test(value);
};

function InputDateTimeComponent({
  id = '',
  name = '',
  value = '',
  onChange = () => null,
  onBlur = () => null,
  onFocus = () => null,
  error = false,
  style = null,
  isFocusOnInit = false,
  disabled = false,
}) {
  const [daySelected, setDaySelected] = useState(false);
  const [monthSelected, setMonthSelected] = useState(false);
  const [yearSelected, setYearSelected] = useState(false);

  const { day: dayInit, month: monthInit, year: yearInit } = getDayMonthYear(value);

  const [day, setDay] = useState(dayInit);
  const [month, setMonth] = useState(monthInit);
  const [year, setYear] = useState(yearInit);

  useEffect(() => {
    setDay(dayInit);
    setMonth(monthInit);
    setYear(yearInit);
  }, [dayInit, monthInit, yearInit]);

  const divRef = useRef(null);
  const [isFocusDiv, setIsFocusDiv] = useState(false);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const dateFormat = useCallback(({ dayC = day, monthC = month, yearC = year }) => {
    if (dayC || monthC || yearC) {
      return `${yearC || ''}-${monthC || ''}-${dayC || ''}`;
    }
    return ''; // ✅ String vide au lieu de null
  }, [day, month, year]);

  const dayHandleChange = useCallback((value) => {
    const dayC = dayTransform({ value });
    setDay(() => dayC);
    return dateFormat({ dayC });
  }, [dateFormat]);

  const monthHandleChange = useCallback((value) => {
    const monthC = monthTransform({ value });
    setMonth(() => monthC);
    return dateFormat({ monthC });
  }, [dateFormat]);

  const yearHandleChange = useCallback((value) => {
    const yearC = yearTransform({ value });
    setYear(() => yearC);
    return dateFormat({ yearC });
  }, [dateFormat]);

  // Optimized callbacks for day input
  const handleDayFocus = useCallback((e) => {
    e.preventDefault();
    e.target?.select();
    setIsFocusDiv(true);
    onFocus();
  }, [onFocus]);

  const handleDayChange = useCallback((e) => {
    const targetValue = e.target.value;
    const date = dayHandleChange(targetValue);
    if (dayTransform({ value: targetValue }) >= 4 || focusNextElement(targetValue)) {
      monthRef.current?.focus();
    }
    onChange(date);
  }, [onChange, dayHandleChange]);

  const handleDayBlur = useCallback((e) => {
    const date = dayHandleChange(e.target.value);
    onBlur(date);
  }, [onBlur, dayHandleChange]);

  const handleDayKeyDown = useCallback((e) => {
    if (e.keyCode === RIGHT_KEY_CODE) {
      monthRef.current?.focus();
      setMonthSelected(true);
    }
    if (e.keyCode === UP_KEY_CODE) {
      e.preventDefault();
      const newDay = Number(day) + 1;
      const date = dayHandleChange(newDay);
      onChange(date);
    }
    if (e.keyCode === DOWN_KEY_CODE) {
      e.preventDefault();
      const newDay = Number(day) - 1;
      const date = dayHandleChange(newDay);
      onChange(date);
    }
  }, [day, onChange, dayHandleChange]);

  // Optimized callbacks for month input
  const handleMonthFocus = useCallback((e) => {
    e.preventDefault();
    e.target?.select();
    onFocus();
  }, [onFocus]);

  const handleMonthChange = useCallback((e) => {
    const targetValue = e.target.value;
    const date = monthHandleChange(targetValue);
    if (monthTransform({ value: targetValue }) >= 2 || focusNextElement(targetValue)) {
      yearRef.current?.focus();
    }
    onChange(date);
  }, [onChange, monthHandleChange]);

  const handleMonthBlur = useCallback((e) => {
    const date = monthHandleChange(e.target.value);
    onBlur(date);
  }, [onBlur, monthHandleChange]);

  const handleMonthKeyDown = useCallback((e) => {
    if (e.keyCode === RIGHT_KEY_CODE) {
      yearRef.current?.focus();
      setYearSelected(true);
    }
    if (e.keyCode === LEFT_KEY_CODE) {
      dayRef.current?.focus();
      setDaySelected(true);
    }
    if (e.keyCode === UP_KEY_CODE) {
      e.preventDefault();
      const newMonth = Number(month) + 1;
      const date = monthHandleChange(newMonth);
      onChange(date);
    }
    if (e.keyCode === DOWN_KEY_CODE) {
      e.preventDefault();
      const newMonth = Number(month) - 1;
      const date = monthHandleChange(newMonth);
      onChange(date);
    }
  }, [month, onChange, monthHandleChange]);

  // Optimized callbacks for year input
  const handleYearFocus = useCallback((e) => {
    e.preventDefault();
    e.target.select();
    onFocus();
  }, [onFocus]);

  const handleYearChange = useCallback((e) => {
    const date = yearHandleChange(e.target.value);
    onChange(date);
  }, [onChange, yearHandleChange]);

  const handleYearBlur = useCallback((e) => {
    const date = yearHandleChange(e.target.value);
    onBlur(date);
  }, [onBlur, yearHandleChange]);

  const handleYearKeyDown = useCallback((e) => {
    if (e.keyCode === LEFT_KEY_CODE) {
      monthRef.current?.focus();
      setMonthSelected(true);
    }
  }, []);

  // Optimized callback for div click
  const handleDivClick = useCallback(() => {
    dayRef.current?.focus();
    setDaySelected(true);
  }, []);

  // Optimized callback for calendar input
  const handleCalendarChange = useCallback((e) => {
    const value = e.target.value;
    if (value) {
      const { day: dayC, month: monthC, year: yearC } = getDayMonthYear(value);

      setYear(yearC);
      setMonth(monthC);
      setDay(dayC);

      const date = dateFormat({ dayC, monthC, yearC });
      onChange(date);
      onBlur(date);
    } else {
      setYear('');
      setMonth('');
      setDay('');
      onChange();
      onBlur();
    }
  }, [onChange, onBlur]);

  useFocusOnInit(dayRef, isFocusOnInit, onFocus);

  useEffect(() => {
    if (daySelected) {
      dayRef.current.select();
      setDaySelected(false);
    }
    if (monthSelected) {
      monthRef.current.select();
      setMonthSelected(false);
    }
    if (yearSelected) {
      yearRef.current.select();
      setYearSelected(false);
    }
  }, [monthSelected, daySelected, yearSelected]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (divRef.current && divRef.current.contains(event.target)) {
        setIsFocusDiv(true);
      } else {
        setIsFocusDiv(false);
      }
    }
    // Bind the event listener
    document.addEventListener('click', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div
      // TODO "Understanding the usefulness of passing IDs in the input"
      id={id}
      ref={divRef}
      className={styles.InputDateTime}
      style={{
        borderColor: error ? 'rgba(255, 91, 97, 1)' : isFocusDiv ? '#7373ff' : 'rgb(220, 223, 230)',
        ...style,
      }}
    >
      <div className="container">
        <div className="item_1">
          <input
            data-cy="input_day-InputDateTime"
            name={name}
            className="input_day"
            ref={dayRef}
            type="number"
            disabled={disabled}
            value={day}
            onFocus={handleDayFocus}
            onChange={handleDayChange}
            onBlur={handleDayBlur}
            onKeyDown={handleDayKeyDown}
            min="1"
            max="31"
            placeholder="jj"
          />{' '}
          /{/*INPUT*/}
          <input
            data-cy="input_month-InputDateTime"
            className="input_month"
            ref={monthRef}
            type="number"
            value={month}
            disabled={disabled}
            onFocus={handleMonthFocus}
            onChange={handleMonthChange}
            onBlur={handleMonthBlur}
            onKeyDown={handleMonthKeyDown}
            min="1"
            max="12"
            placeholder="mm"
          />{' '}
          /
          <input
            data-cy="input_year-InputDateTime"
            className="input_year"
            ref={yearRef}
            type="number"
            value={year}
            disabled={disabled}
            onFocus={handleYearFocus}
            onChange={handleYearChange}
            onBlur={handleYearBlur}
            onKeyDown={handleYearKeyDown}
            min="1000"
            max="9999"
            placeholder="aaaa"
          />
          <div
            className="item_2"
            onClick={handleDivClick}
          />
        </div>
        <input
          data-cy="input_calendar-InputDateTime"
          className="calendar"
          onFocus={onFocus}
          disabled={disabled}
          value={dateFormat({}) || ''}
          onChange={handleCalendarChange}
          type="date"
        />
      </div>
    </div>
  );
}


InputDateTimeComponent.propTypes = {
  isFocusOnInit: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  value: PropTypes.string,
};

const InputDateTime = InputDateTimeComponent;

export default InputDateTime;
